import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { DashboardService } from './services/dashboard.service';
import { DashboardData } from './interfaces/dashboard.interface';
import { getQuoteStatusLabel, getQuoteStatusBadgeClass } from '@quotes/constants/quote.constants';
import { CardComponent } from '@shared/components/ui/card/card.component';
import { KPICardComponent } from '@shared/components/ui/kpi-card/kpi-card.component';
import { AuthService } from '@auth/services/auth.service';
import { environment } from '@environments/environment';
import { BannerCarouselComponent } from '@shared/components/ui/banner-carousel/banner-carousel.component';

@Component({
  selector: 'app-home-page',
  standalone: true,
  imports: [CommonModule, RouterLink, KPICardComponent, CardComponent, BannerCarouselComponent],
  templateUrl: './home-page.component.html',
})
export class HomePageComponent implements OnInit {
  private readonly dashboardService = inject(DashboardService);
  private readonly authService = inject(AuthService);
  readonly user = this.authService.user;

  readonly dashboardData = signal<DashboardData | null>(null);
  readonly isLoading = signal<boolean>(true);
  readonly error = signal<string | null>(null);
  readonly showDashboard = environment.showDashboard;

  // Exponer funciones para el template
  readonly getQuoteStatusLabel = getQuoteStatusLabel;
  readonly getQuoteStatusBadgeClass = getQuoteStatusBadgeClass;
  readonly Math = Math;

  ngOnInit(): void {
    if (this.showDashboard) {
      this.loadDashboardData();
    } else {
      this.isLoading.set(false);
    }
  }

  loadDashboardData(): void {
    this.isLoading.set(true);
    this.error.set(null);

    this.dashboardService.getDashboardData().subscribe({
      next: (data) => {
        this.dashboardData.set(data);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.error.set('Error al cargar los datos del dashboard');
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Formatea un número como moneda dominicana
   */
  formatCurrency(value: number): string {
    if (value === null || value === undefined || isNaN(value)) {
      return 'RD$0.00';
    }
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  /**
   * Formatea un número grande como moneda abreviada (ej: 12.4M)
   */
  formatCurrencyShort(value: number): string {
    if (value === null || value === undefined || isNaN(value)) {
      return 'RD$0';
    }
    if (value >= 1000000) {
      return `RD$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `RD$${(value / 1000).toFixed(1)}K`;
    }
    return this.formatCurrency(value);
  }

  /**
   * Formatea un porcentaje
   */
  formatPercent(value: number): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '0%';
    }
    return `${value.toFixed(1)}%`;
  }

  /**
   * Formatea una fecha corta
   */
  formatDateShort(date: string | null): string {
    if (!date) return 'N/A';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return 'N/A';
      return d.toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  }

  /**
   * Formatea una fecha relativa (Hoy, Ayer, o fecha)
   */
  formatDateRelative(date: string | null): string {
    if (!date) return 'N/A';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return 'N/A';

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const dateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());

      const timeStr = d.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' });

      if (dateOnly.getTime() === today.getTime()) {
        return `Hoy, ${timeStr}`;
      } else if (dateOnly.getTime() === yesterday.getTime()) {
        return `Ayer, ${timeStr}`;
      } else {
        return d.toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
    } catch {
      return 'N/A';
    }
  }

  /**
   * Calcula el porcentaje de cambio (simulado - en producción vendría del API)
   */
  getChangePercentage(current: number, previous: number): number {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  }

  /**
   * Obtiene el color de la clase para el porcentaje de cambio
   */
  getChangeColorClass(percentage: number): string {
    if (percentage > 0) return 'text-success';
    if (percentage < 0) return 'text-error';
    return 'text-base-content/70';
  }

  /**
   * Calcula la altura máxima para el gráfico de tendencia
   */
  getMaxTrendValue(data: { quotes: number; approved: number }[]): number {
    if (!data || data.length === 0) return 100;
    const maxQuotes = Math.max(...data.map((d) => d.quotes));
    const maxApproved = Math.max(...data.map((d) => d.approved));
    return Math.max(maxQuotes, maxApproved, 1);
  }

  /**
   * Calcula el porcentaje de altura para un valor en el gráfico de tendencia
   */
  getTrendHeight(value: number, maxValue: number): number {
    if (maxValue === 0) return 0;
    return (value / maxValue) * 100;
  }

  /**
   * Calcula las coordenadas Y para un gráfico de líneas
   * Usa viewBox 0 0 600 200 con coordenadas absolutas
   */
  getLineY(value: number, maxValue: number): number {
    if (maxValue === 0) return 180; // Cerca del fondo si no hay valor
    // Usar 20-180 como rango (margen de 20 arriba y abajo)
    const minY = 20;
    const maxY = 180;
    const range = maxY - minY;
    return maxY - (value / maxValue) * range;
  }

  /**
   * Genera el path SVG para una línea del gráfico de tendencia
   * Usa viewBox 0 0 600 200 con coordenadas absolutas
   */
  getLinePath(
    data: { quotes: number; approved: number }[],
    maxValue: number,
    type: 'quotes' | 'approved'
  ): string {
    if (!data || data.length === 0) return '';
    const points = data.map((point, index) => {
      const value = type === 'quotes' ? point.quotes : point.approved;
      // Dejar margen izquierdo (40px) y derecho (40px), usar 520px para el gráfico
      const x = data.length > 1 ? 40 + (index / (data.length - 1)) * 520 : 40;
      const y = this.getLineY(value, maxValue);
      return `${x},${y}`;
    });
    return `M ${points.join(' L ')}`;
  }

  /**
   * Obtiene la coordenada X para un punto en el gráfico
   * Con margen izquierdo (40px) y derecho (40px)
   */
  getLineX(index: number, total: number): number {
    if (total <= 1) return 40; // Margen izquierdo
    return 40 + (index / (total - 1)) * 520; // 40px margen izquierdo, 520px para el gráfico
  }

  /**
   * Formatea el número de cotización
   * Solo devuelve el número sin prefijos
   */
  formatQuoteNumber(quoteNumber: number): string {
    if (!quoteNumber) return 'N/A';
    return quoteNumber.toString();
  }

  /**
   * Calcula los datos del gráfico de pastel para la distribución por estado
   */
  getPieChartData(
    distribution: { label: string; value: number; percentage: number; color: string }[]
  ): Array<{ label: string; percentage: number; angle: number; offset: number; color: string }> {
    if (!distribution || distribution.length === 0) return [];

    // Calcular el total de porcentajes para normalizar si no suman exactamente 100%
    const totalPercentage = distribution.reduce((sum, item) => sum + item.percentage, 0);
    const normalizationFactor = totalPercentage > 0 ? 100 / totalPercentage : 1;

    let currentOffset = 0;
    const circumference = 2 * Math.PI * 40; // Radio del círculo es 40

    return distribution.map((item) => {
      // Normalizar el porcentaje si es necesario
      const normalizedPercentage = item.percentage * normalizationFactor;
      const angle = (normalizedPercentage / 100) * 360;
      const offset = currentOffset;
      currentOffset += angle;

      const color =
        item.color === 'success' ? '#10b981' : item.color === 'warning' ? '#f59e0b' : '#ef4444';

      return {
        label: item.label,
        percentage: normalizedPercentage,
        angle,
        offset,
        color,
      };
    });
  }

  /**
   * Calcula el valor máximo de count en la distribución por tipo de vehículo
   */
  getMaxVehicleTypeCount(
    distribution: { type: string; count: number; percentage: number }[]
  ): number {
    if (!distribution || distribution.length === 0) return 1;
    return Math.max(...distribution.map((v) => v.count), 1);
  }
}
